/**
 * Uitleg bij termen zonder (i)-icoontjes (besluit Asil, 1 oktober 2026; vorm herzien 2 oktober 2026).
 *
 * Een term met uitleg (`HelpTerm`) krijgt een subtiele stippellijn. Onderaan het venster of menu staat altijd
 * een uitlegvak (`HelpArea`). Een klik of tik op een term (of Enter/spatie met het toetsenbord) klapt het vak
 * open en toont de uitleg volledig, zonder scrollbalk. Met het pijltje rechts klapt de gebruiker het weer dicht;
 * dichtgeklapt toont het vak één regel (de laatst gekozen term of een korte aanwijzing). Aanwijzen met de muis
 * verandert niets meer: dat liet het menu verspringen en flitsen.
 *
 * Knoppen houden hun gewone tooltip; dit is voor begrippen, veldnamen en kolomkoppen.
 *
 * Gebruik:
 *   <HelpArea>                                   // elk ActionPopover heeft er al een
 *     <HelpTerm help="Uitvoeringsmaterialiteit: …">UM</HelpTerm>
 *   </HelpArea>
 */

import * as React from "react";
import { shellText } from "./shellText";
import { useLang } from "../../i18n/LangContext";
import "./pane.css";

interface HelpEntry {
  label: string;
  text: React.ReactNode;
}

interface HelpContextValue {
  show: (entry: HelpEntry) => void;
  register: () => () => void;
}

const HelpContext = React.createContext<HelpContextValue | null>(null);

export interface HelpAreaProps {
  children: React.ReactNode;
  /** Tekst als er nog niets is gekozen; standaard een korte aanwijzing. */
  idleText?: React.ReactNode;
  className?: string;
}

/**
 * Venster met een vast uitlegvak onderaan. Het vak verschijnt alleen als er in het venster minstens één
 * `HelpTerm` staat; anders kost het geen ruimte.
 */
export function HelpArea({ children, idleText, className }: HelpAreaProps): JSX.Element {
  const [entry, setEntry] = React.useState<HelpEntry | null>(null);
  const [open, setOpen] = React.useState(false);
  const [terms, setTerms] = React.useState(0);
  const lang = useLang();

  const value = React.useMemo<HelpContextValue>(
    () => ({
      show: (e) => {
        setEntry(e);
        setOpen(true);
      },
      register: () => {
        setTerms((n) => n + 1);
        return () => setTerms((n) => n - 1);
      },
    }),
    []
  );

  const hint = idleText ?? shellText("helpHint", lang);
  const canOpen = entry !== null;

  return (
    <HelpContext.Provider value={value}>
      <div className={`pane-helparea${className ? ` ${className}` : ""}`}>
        <div className="pane-helparea__content">{children}</div>
        {terms > 0 ? (
          <div
            className={`pane-helparea__box${open && canOpen ? " is-open" : ""}`}
            aria-live="polite"
          >
            <div className="pane-helparea__bar">
              <span className="pane-helparea__label">
                {entry ? (
                  <strong>{entry.label}</strong>
                ) : (
                  <span className="pane-helparea__hint">{hint}</span>
                )}
              </span>
              {canOpen ? (
                <button
                  type="button"
                  className="pane-helparea__toggle"
                  onClick={() => setOpen((o) => !o)}
                  aria-expanded={open}
                  aria-label={shellText(open ? "helpCollapse" : "helpExpand", lang)}
                  title={shellText(open ? "collapse" : "expand", lang)}
                >
                  <span aria-hidden="true">{open ? "▾" : "▸"}</span>
                </button>
              ) : null}
            </div>
            {open && entry ? <div className="pane-helparea__text">{entry.text}</div> : null}
          </div>
        ) : null}
      </div>
    </HelpContext.Provider>
  );
}

export interface HelpTermProps {
  /** De uitleg; één of meer zinnen. */
  help: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

/** Platte tekst van de term, voor de kop van het uitlegvak. */
function textOf(node: React.ReactNode): string {
  if (node === null || node === undefined || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join("");
  if (React.isValidElement(node))
    return textOf((node.props as { children?: React.ReactNode }).children);
  return "";
}

/**
 * Begrip met uitleg. Buiten een `HelpArea` valt het terug op een gewone tooltip (alleen bij tekst),
 * zodat de uitleg nooit verloren gaat.
 */
export function HelpTerm({ help, children, className }: HelpTermProps): JSX.Element {
  const ctx = React.useContext(HelpContext);
  const lang = useLang();
  React.useEffect(() => (ctx ? ctx.register() : undefined), [ctx]);

  const cls = `pane-helpterm${className ? ` ${className}` : ""}`;
  if (!ctx) {
    return (
      <span className={cls} title={typeof help === "string" ? help : undefined}>
        {children}
      </span>
    );
  }
  const show = (): void =>
    ctx.show({ label: textOf(children).trim() || shellText("explanation", lang), text: help });
  return (
    <span
      className={cls}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          show();
        }
      }}
      // Binnen een label of summary niet doorgeven, anders vinkt een tik het vakje aan, klapt het blok open of
      // springt de focus naar het invoerveld.
      onClick={(e) => {
        if ((e.currentTarget as HTMLElement).closest("label, summary")) {
          e.preventDefault();
          e.stopPropagation();
        }
        show();
      }}
    >
      {children}
    </span>
  );
}
